import { useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '@/components/base/BreadcrumbNav';

interface SEOSchemaProps {
  pageType: 'WebPage' | 'Article' | 'AboutPage';
  pageTitle: string;
  pageDescription: string;
  canonicalUrl: string;
  breadcrumbItems?: BreadcrumbItem[];
  articleData?: {
    headline: string;
    description: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
    imageUrl: string;
  };
  faqData?: { question: string; answer: string }[];
}

export default function SEOSchema({
  pageType,
  pageTitle,
  pageDescription,
  canonicalUrl,
  breadcrumbItems,
  articleData,
  faqData,
}: SEOSchemaProps) {
  const location = useLocation();

  const buildBreadcrumbSchema = (): Record<string, unknown> | null => {
    if (!breadcrumbItems || breadcrumbItems.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.to ? `https://koreancomics.cn${item.to}` : undefined,
      })),
    };
  };

  const buildWebPageSchema = (): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@type': pageType,
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画在线',
      url: 'https://koreancomics.cn/',
    },
  });

  const buildArticleSchema = (): Record<string, unknown> | null => {
    if (!articleData) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleData.headline,
      description: articleData.description,
      image: articleData.imageUrl,
      datePublished: articleData.datePublished,
      dateModified: articleData.dateModified,
      author: {
        '@type': 'Person',
        name: articleData.authorName,
      },
      publisher: {
        '@type': 'Organization',
        name: '韩国漫画在线',
        url: 'https://koreancomics.cn/',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
    };
  };

  const buildFAQSchema = (): Record<string, unknown> | null => {
    if (!faqData || faqData.length === 0) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  };

  const schemas = [
    buildWebPageSchema(),
    buildBreadcrumbSchema(),
    buildArticleSchema(),
    buildFAQSchema(),
  ].filter(Boolean);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemas.length === 1 ? schemas[0] : schemas),
        }}
      />
    </>
  );
}